/**
 * Toggle — přepínač pro booleovské nastavení (LHUI: jemný stín track + knob).
 *
 * Ukládá stav ihned při kliknutí přes onSave callback.
 * Lokálně optimisticky přepne, onSave.onError vrátí původní hodnotu.
 *
 * Použití:
 *   <Toggle value={cfg.enabled === 'true'} onSave={v => saveCfg({ enabled: v ? 'true' : 'false' })} />
 *   <Toggle value={running} label="Daemon" onSave={onToggleDaemon} saving={saving} />
 */

import { useEffect, useState } from 'react';
import { cn } from '../../lib/utils';

interface ToggleProps {
  /** Aktuální hodnota */
  value: boolean;
  /** Zavolá se ihned při kliknutí s novou hodnotou. Může být async. */
  onSave: (newValue: boolean) => void | Promise<void>;
  /** Zobrazit popisek vedle přepínače */
  label?: string;
  /** Blokuje klikání (probíhá jiný save) */
  saving?: boolean;
  disabled?: boolean;
  className?: string;
}

export function Toggle({ value, onSave, label, saving, disabled, className }: ToggleProps) {
  const [optimistic, setOptimistic] = useState(value);

  // Sync na změnu zvenku (po revalidaci dat)
  useEffect(() => {
    setOptimistic(value);
  }, [value]);

  const handleClick = async () => {
    if (saving || disabled) return;
    const next = !optimistic;
    setOptimistic(next);
    try {
      await onSave(next);
    } catch {
      setOptimistic(optimistic); // rollback
    }
  };

  return (
    <label
      className={cn(
        'inline-flex items-center gap-2 cursor-pointer select-none',
        (saving || disabled) && 'opacity-40 cursor-not-allowed',
        className,
      )}
    >
      {label && <span className='text-[11px] text-gray-500'>{label}</span>}
      <button
        type='button'
        role='switch'
        aria-checked={optimistic}
        onClick={handleClick}
        disabled={saving || disabled}
        className={cn(
          'relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-all duration-150 focus:outline-none',
          'focus-visible:ring-2 focus-visible:ring-plex/40 focus-visible:ring-offset-1 focus-visible:ring-offset-transparent',
          optimistic
            ? 'bg-plex acm-toggle-track-on'
            : 'acm-toggle-track',
        )}
      >
        <span
          className={cn(
            'inline-block h-3.5 w-3.5 transform rounded-full bg-white acm-toggle-knob transition-transform duration-150',
            optimistic ? 'translate-x-[18px]' : 'translate-x-0.5',
          )}
        />
      </button>
    </label>
  );
}
